import React, { Fragment, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { Button } from "react-bootstrap";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import { productActions } from "../../../_store";
import Toastr from "../../components/common/Toastr";
import emptyImage from "../../../images/no-image-png-2.png";
import { fCurrency } from "../../../utils/formatNumber";

const { REACT_APP_API_IMAGE_URL } = process.env;

const GeneralTab = () => {
  const dispatch = useDispatch();
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const inputRef = useRef(null);

  // #region Reducers ------------------  Reducers --------------------------------

  // Master Data Selectors
  const {
    warehouseDrpList,
    categoryDrpList,
    subCategoryDrpList,
    shippingMethodDrpList,
    packageTypeDrpList,
  } = useSelector((state) => state.master);

  // Product Data Selectors
  const generalTabData = useSelector((x) => x.product.productTabList);
  const { auth } = useSelector((state) => state);

  // #end region Reducers ------------------  Reducers End --------------------------------

  let styles = {
    control: (styles) => ({ ...styles, border: "1px solid red" }),
  };

  const [subCategoryList, setSubCategoryList] = useState([]);
  const submitted = generalTabData && generalTabData.setSubmitted;

  const [locationAdd, setLocationAdd] = useState(false);
  const currentUser = auth.user;

  // Define regex patterns outside the function
  const regexCurrency = /^(\$?[$ 0-9,.]+)$/;
  const regexDecimal = /^[+-]?((\d+(\.\d*)?)|(\.\d+))$/;
  const regexNoSymbol = /^[A-Za-z0-9]+$/;
  const regexNumber = /^[0-9\b]+$/;

  // Helper function to validate currency
  const validateCurrency = (value) => {
    if (!regexCurrency.test(value)) return false;

    let currencyValue;
    try {
      currencyValue = parseFloat(value.replace(/\$|,/g, ""));
    } catch {
      return false;
    }
    currencyValue = currencyValue.toString();

    return (
      (!value.includes(".") && currencyValue.length <= 6) ||
      (value.includes(".") &&
        currencyValue.split(".")[0].length <= 6 &&
        value.split(".")[1].length <= 2)
    );
  };

  // Helper function to validate decimal values
  const validateDecimal = (value) => {
    return (
      regexDecimal.test(value) &&
      ((!value.includes(".") && value.length <= 6) ||
        (value.includes(".") &&
          value.split(".")[0].length <= 6 &&
          value.split(".")[1].length <= 2))
    );
  };

  // Helper function to validate levels
  const validateLevel = (value) => {
    if (!regexNoSymbol.test(value)) return false;

    const [letter, number1, number2] = value.split("");
    if (!/^[A-Z]$/.test(letter.toUpperCase())) return false;

    if (number1) {
      if (!/^[1-9]$/.test(number1)) return false;

      if (number2) {
        return /^[0-9]$/.test(number2) && parseInt(number1 + number2) <= 20;
      }
      return true;
    }
    return true;
  };

  // Main handleChange function
  const handleChange = (event) => {
    event.preventDefault();
    const newFormData = { ...generalTabData };
    const fieldName = event.target.getAttribute("name");
    const fieldValue = event.target.value.trim();

    if (fieldValue) {
      switch (fieldName) {
        case "Cost":
        case "RegularPrice":
        case "GPARTNERPrice":
        case "GStarPrice":
        case "PFAPrice":
        case "GVendorPrice":
        case "GAssociatePrice":
        case "PartyDecoPrice":
          if (validateCurrency(fieldValue)) {
            newFormData[fieldName] = fieldValue;
          }
          break;

        case "Weight":
        case "DimensionLength":
        case "DimensionWidth":
        case "DimensionHeight":
          if (validateDecimal(fieldValue)) {
            newFormData[fieldName] = fieldValue;
          }
          break;

        case "PerBoxCount":
        case "WarrantyPeriod":
          if (regexNumber.test(fieldValue)) {
            if (fieldValue > 0) {
              newFormData[fieldName] = fieldValue;
            }
          }
          break;

        case "Aisle":
        case "MinimumQuantity":
        case "Bay":
          if (regexNumber.test(fieldValue) && fieldValue !== "00") {
            newFormData[fieldName] = fieldValue;
          }
          break;

        case "Level":
          if (validateLevel(fieldValue)) {
            newFormData[fieldName] = fieldValue.toUpperCase();
          }
          break;

        case "ProductName":
          if (!fieldValue.includes(":")) {
            newFormData[fieldName] = event.target.value;
          }
          break;

        default:
          newFormData[fieldName] = event.target.value;
          break;
      }
    } else {
      newFormData[fieldName] = fieldValue;
    }

    dispatch(productActions.tempSetProductTabData(newFormData));
  };

  const handleLocationChange = (event, index) => {
    event.preventDefault();

    let newFormData = { ...generalTabData };
    let oldTypeLocationList = [...newFormData.TypeLocationList];

    let locationRow = { ...oldTypeLocationList[index] };

    const fieldName = event.target.getAttribute("name");
    const fieldValue = event.target.value.trim();

    if (fieldValue) {
      switch (fieldName) {
        case "Aisle":
        case "Bay":
          if (regexNumber.test(fieldValue) && fieldValue !== "00") {
            locationRow[fieldName] = fieldValue;
          }
          break;

        case "Level":
          if (validateLevel(fieldValue)) {
            locationRow[fieldName] = fieldValue.toUpperCase();
          }
          break;
        default:
          locationRow[fieldName] = event.target.value;
          break;
      }
    } else {
      locationRow[fieldName] = fieldValue;
    }
    oldTypeLocationList[index] = { ...locationRow };
    newFormData.TypeLocationList = [...oldTypeLocationList];
    dispatch(productActions.tempSetProductTabData(newFormData));
  };

  // Assuming fCurrency is a function that formats currency values
  const handleBlur = (event) => {
    const fieldName = event.target.getAttribute("name");
    const fieldValue = event.target.value.trim();

    // Create a new form data object with updated value
    const newFormData = { ...generalTabData };

    // List of fields that require currency formatting
    const currencyFields = [
      "Cost",
      "RegularPrice",
      "GPARTNERPrice",
      "GStarPrice",
      "PFAPrice",
      "GVendorPrice",
      "GAssociatePrice",
      "PartyDecoPrice",
    ];

    // Check if the fieldName is in the list of currency fields
    if (currencyFields.includes(fieldName)) {
      newFormData[fieldName] = fCurrency(fieldValue);
    } else {
      newFormData[fieldName] = fieldValue;
    }

    // Dispatch action to update form data
    dispatch(productActions.tempSetProductTabData(newFormData));
  };

  // handle Dropdown Change function
  const handleDrpChange = (event, title) => {
    const value = parseInt(event.value);
    const label = event.label;

    // Create a new form data object with updated value
    const newFormData = { ...generalTabData };

    // Define a mapping for dropdown titles to option fields
    const optionFields = {
      WarehouseID: "WarehouseOption",
      CategoryID: "CategoryOption",
      SubCategoryID: "SubCategoryOption",
      ShippingMethodID: "ShippingMethodOption",
      PackageTypeID: "PackageTypeOption",
    };

    // Set the corresponding option field
    if (optionFields[title]) {
      newFormData[optionFields[title]] = { value, label };
    }

    // Special handling for CategoryID
    if (title === "CategoryID" && value > 0) {
      const subCategory = subCategoryDrpList.filter(
        (d) => d.parentId === value
      );
      setSubCategoryList(subCategory);
      newFormData.SubCategoryOption = null;
      newFormData.SubCategoryID = 0;
    }

    // Set the form data value for the specific title
    newFormData[title] = value;

    // Dispatch the updated form data
    dispatch(productActions.tempSetProductTabData(newFormData));
  };

  const handleChkBoxChangeWooCom = (event) => {
    const { name, checked } = event.target;

    const newFormData = { ...generalTabData, [name]: checked };

    dispatch(productActions.tempSetProductTabData(newFormData));
  };

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => resolve(fileReader.result);
      fileReader.onerror = (error) => reject(error);
    });
  };

  const UploadImages = async (e) => {
    const files = e.target.files;

    // Check if files are selected
    if (files.length === 0) return;

    const oldGeneralTabData = { ...generalTabData };
    const existingImages = oldGeneralTabData.TypeProductImageModelList.filter(
      (d) => !d.IsDeleted
    );

    // Validate the number of images
    if (existingImages.length >= 5) {
      Toastr("You can include a maximum of five product images.", "warning");
      return;
    }

    // Process each file
    for (const file of files) {
      const fileType = file.type;
      const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
      const MAX_FILE_SIZE = 5120; // 5MB
      const fileSizeKiloBytes = file.size / 1024;

      // Validate file type
      if (!validImageTypes.includes(fileType)) {
        hideLoader();
        Toastr("Invalid file format selected", "warning");
        continue; // Skip to the next file
      }

      // Validate file size
      if (fileSizeKiloBytes > MAX_FILE_SIZE) {
        Toastr("File size is greater than maximum 5MB", "warning");
        continue; // Skip to the next file
      }

      // Show loader
      showLoader();

      try {
        // Convert file to Base64
        const base64 = await convertBase64(file);

        // Prepare new image object
        const newProductImage = {
          ProductImageID: 0,
          ProductID: 0,
          ImageFilePath: base64,
          WCImageURL: "",
          PDWCImageURL: "",
          Status: 1,
          IsDeleted: false,
        };

        // Update image list
        oldGeneralTabData.TypeProductImageModelList = [
          ...existingImages,
          newProductImage,
        ];

        // Dispatch updated form data
        dispatch(productActions.tempSetProductTabData(oldGeneralTabData));
      } catch (error) {
        // Handle conversion errors
        Toastr("An error occurred while processing the image.", "error");
      } finally {
        // Hide loader
        hideLoader();
      }
    }
  };

  const handleClickPAImageClose = (index, ProductImageID) => {
    // Ensure index is valid
    if (index < 0 || index >= generalTabData.TypeProductImageModelList.length) {
      console.error("Invalid index");
      return;
    }

    const oldGeneralTabData = { ...generalTabData };
    const oldProductImageList = [
      ...oldGeneralTabData.TypeProductImageModelList,
    ];

    if (ProductImageID > 0) {
      // Mark image as deleted
      const rowData = { ...oldProductImageList[index] };
      rowData.IsDeleted = true;
      oldProductImageList[index] = { ...rowData };
    } else {
      // Remove image from the list
      oldProductImageList.splice(index, 1);
    }

    // Update the form data with the new image list
    oldGeneralTabData.TypeProductImageModelList = oldProductImageList;
    dispatch(productActions.tempSetProductTabData(oldGeneralTabData));

    // Clear the file input and hide the loader
    if (inputRef.current) {
      inputRef.current.value = null;
    }
    hideLoader();
  };

  const handleAddLocation = () => {
    let oldGeneralTabData = { ...generalTabData };
    const IsLocationExist = generalTabData.TypeLocationList.some(
      (item, index) =>
        (!item.Aisle ||
          item.Aisle === "0" ||
          item?.Aisle?.length !== 2 ||
          !item.Bay ||
          item.Bay === "0" ||
          item?.Bay?.length !== 2 ||
          !item.Level) &&
        item.IsDeleted === false
    );

    if (IsLocationExist) {
      setLocationAdd(true);
      Toastr("Please fill in the required missing fields", "warning");
    } else {
      let isLocationDuplicate = false;
      if (generalTabData?.TypeLocationList?.length > 1) {
        for (let item of generalTabData.TypeLocationList) {
          let location = item.Aisle + item.Bay + item.Level;
          let filterLocation = generalTabData.TypeLocationList.filter(
            (item) =>
              item.Aisle + item.Bay + item.Level === location &&
              item.IsDeleted === false
          );

          if (filterLocation?.length > 1) {
            isLocationDuplicate = true;
            Toastr(
              "please check previous data location is already used",
              "warning"
            );
            break;
          }
        }
      }

      if (!isLocationDuplicate) {
        let oldTypeLocationList = [...oldGeneralTabData.TypeLocationList];

        let newLocation = {
          ProductLocationID: 0,
          ProductID: generalTabData.ProductID,
          Aisle: "",
          Level: "",
          Bay: "",
          IsDeleted: false,
        };

        oldTypeLocationList.push(newLocation);
        oldGeneralTabData.TypeLocationList = oldTypeLocationList;
        dispatch(productActions.tempSetProductTabData(oldGeneralTabData));
        setLocationAdd(false);
      }
    }
  };

  const handleRemoveLocaton = (index) => {
    let oldGeneralTabData = { ...generalTabData };

    const oldLocationList = [...oldGeneralTabData.TypeLocationList];

    if (oldGeneralTabData?.ProductID > 0) {
      const rowData = { ...oldLocationList[index] };
      if (rowData?.ProductLocationID > 0) {
        rowData.IsDeleted = true;
        oldLocationList[index] = { ...rowData };
      } else {
        oldLocationList.splice(index, 1);
      }
    } else {
      oldLocationList.splice(index, 1);
    }

    oldGeneralTabData.TypeLocationList = oldLocationList;
    dispatch(productActions.tempSetProductTabData(oldGeneralTabData));
  };

  const tabChange = () => {
    dispatch(productActions.tempActiveProductTab("Inventory"));
  };

  return (
    <>
      <div className="tab-content">
        <div className="pt-3">
          <div className="General-form">
            {/* <h4 className="text-primary">General</h4> */}

            <div className="row">
              <div className="form-group mb-3 col-md-1">
                <input
                  className={`form-check-input ${
                    submitted &&
                    generalTabData.IsGemar === false &&
                    generalTabData.IsPartyDeco === false &&
                    "border-danger"
                  }`}
                  type="checkbox"
                  role="switch"
                  id="flexSwitchCheckGemar"
                  name="IsGemar"
                  checked={generalTabData.IsGemar === true}
                  disabled={currentUser.UserTypeID === 8} // ✅ disable if user type = 8
                  onChange={(event) => handleChkBoxChangeWooCom(event)}
                />
                <label
                  className="form-check-label font-w700"
                  htmlFor="flexSwitchCheckGemar"
                >
                  Gemar
                </label>
              </div>

              <div className="form-group mb-3 col-md-2">
                <input
                  className={`form-check-input ${
                    submitted &&
                    generalTabData.IsGemar === false &&
                    generalTabData.IsPartyDeco === false &&
                    "border-danger"
                  }`}
                  type="checkbox"
                  role="switch"
                  id="flexSwitchCheckPartyDeco"
                  name="IsPartyDeco"
                  checked={generalTabData.IsPartyDeco === true}
                  disabled={currentUser.UserTypeID === 8} // ✅ disable if user type = 8
                  onChange={(event) => handleChkBoxChangeWooCom(event)}
                />
                <label
                  className="form-check-label font-w700"
                  htmlFor="flexSwitchCheckPartyDeco"
                >
                  PartyDeco
                </label>
              </div>
            </div>
            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Warehouse <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix={"custom_select_css"}
                  name="WarehouseID"
                  value={generalTabData.WarehouseOption}
                  options={warehouseDrpList}
                  onChange={(event) => handleDrpChange(event, "WarehouseID")}
                  isDisabled={currentUser.UserTypeID === 8} // ✅ disable when usertype = 8
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor:
                        currentUser.UserTypeID === 8 ? "#f5f5f5" : "white", // grey out
                      cursor:
                        currentUser.UserTypeID === 8
                          ? "not-allowed"
                          : "default",
                      opacity: currentUser.UserTypeID === 8 ? 0.7 : 1, // faded look
                    }),
                  }}
                />
              </div>

              <div className="col-md-1 " style={{ marginTop: "36px" }}>
                <div className="addButtonLocation">
                  <h4 className="text-primary">Location</h4>
                  {generalTabData?.TypeLocationList &&
                    generalTabData?.TypeLocationList?.length < 8 && (
                      <Button
                        onClick={
                          currentUser.UserTypeID !== 8
                            ? () => handleAddLocation()
                            : undefined
                        }
                        title={
                          currentUser.UserTypeID === 8
                            ? "Not allowed"
                            : "Location"
                        }
                        className="btn btn-primary-color shadow btn-xs sharp me-1"
                        disabled={currentUser.UserTypeID === 8}
                        style={{
                          backgroundColor:
                            currentUser.UserTypeID === 8 ? "#E0E0E0" : "",
                          cursor:
                            currentUser.UserTypeID === 8
                              ? "not-allowed"
                              : "pointer",
                          opacity: currentUser.UserTypeID === 8 ? 0.6 : 1,
                        }}
                      >
                        <i className="fa fa-plus"></i>
                      </Button>
                    )}
                </div>
              </div>
            </div>
            <div className="card p-3">
              <>
                {generalTabData?.TypeLocationList?.map(
                  (item, index) =>
                    item.IsDeleted === false && (
                      <div key={index} className="row">
                        <div className="form-group mb-3 col-md-4">
                          {index === 0 && (
                            <label className="text-black font-w500">
                              Aisle(01 - 99)
                              <span className="text-danger">*</span>
                            </label>
                          )}

                          <input
                            type="text"
                            className={`form-control ${
                              (submitted || locationAdd) &&
                              (!item.Aisle ||
                                item.Aisle === "0" ||
                                item.Aisle.length !== 2)
                                ? "border-danger"
                                : ""
                            }`}
                            autoComplete="off"
                            name="Aisle"
                            required
                            onChange={(e) =>
                              currentUser.UserTypeID !== 8 &&
                              handleLocationChange(e, index)
                            }
                            value={item.Aisle}
                            placeholder="Aisle"
                            minLength={2}
                            maxLength={2}
                            disabled={currentUser.UserTypeID === 8} // 🚫 disable if UserTypeID = 8
                            style={{
                              backgroundColor:
                                currentUser.UserTypeID === 8
                                  ? "#f5f5f5"
                                  : "white",
                              cursor:
                                currentUser.UserTypeID === 8
                                  ? "not-allowed"
                                  : "text",
                            }}
                          />

                          <span className="validation-text"></span>
                        </div>
                        <div className="form-group mb-3 col-md-4">
                          {index === 0 && (
                            <label className="text-black font-w500">
                              Level(A-Z)(1-20)
                              <span className="text-danger">*</span>
                            </label>
                          )}
                          <input
                            type="text"
                            className={`form-control ${
                              (submitted || locationAdd) &&
                              (!item.Level || item.Level.length === 1)
                                ? "border-danger"
                                : ""
                            }`}
                            autoComplete="off"
                            name="Level"
                            required
                            onChange={(e) =>
                              currentUser.UserTypeID !== 8 &&
                              handleLocationChange(e, index)
                            }
                            value={item.Level}
                            placeholder="Level"
                            maxLength={3}
                            disabled={currentUser.UserTypeID === 8} // 🚫 disable if UserTypeID = 8
                            style={{
                              backgroundColor:
                                currentUser.UserTypeID === 8
                                  ? "#f5f5f5"
                                  : "white",
                              cursor:
                                currentUser.UserTypeID === 8
                                  ? "not-allowed"
                                  : "text",
                            }}
                          />
                          <span className="validation-text"></span>
                        </div>

                        <div className="form-group mb-3 col-md-4">
                          {index === 0 && (
                            <label className="text-black font-w500">
                              Bay(01 - 99)<span className="text-danger">*</span>
                            </label>
                          )}
                          <div className="tracking-number-delete">
                            <input
                              type="text"
                              className={`form-control ${
                                (submitted || locationAdd) &&
                                (!item.Bay ||
                                item.Bay === "0" ||
                                item.Bay.length !== 2
                                  ? "border-danger"
                                  : "")
                              }`}
                              autoComplete="off"
                              name="Bay"
                              required
                              onChange={(e) =>
                                currentUser.UserTypeID !== 8 &&
                                handleLocationChange(e, index)
                              }
                              value={item.Bay}
                              placeholder="Bay"
                              minLength={2}
                              maxLength={2}
                              disabled={currentUser.UserTypeID === 8} // 🚫 disable if UserTypeID = 8
                              style={{
                                backgroundColor:
                                  currentUser.UserTypeID === 8
                                    ? "#f5f5f5"
                                    : "white",
                                cursor:
                                  currentUser.UserTypeID === 8
                                    ? "not-allowed"
                                    : "text",
                              }}
                            />
                            <span className="validation-text"></span>

                            {index !== 0 && (
                              <div
                                onClick={() =>
                                  currentUser.UserTypeID !== 8 &&
                                  handleRemoveLocaton(index)
                                }
                                title="Delete"
                                className={`btn btn-danger-color shadow btn-xs sharp me-1 ${
                                  currentUser.UserTypeID === 8 ? "disabled" : ""
                                }`}
                                style={{
                                  pointerEvents:
                                    currentUser.UserTypeID === 8
                                      ? "none"
                                      : "auto",
                                  opacity:
                                    currentUser.UserTypeID === 8 ? 0.5 : 1,
                                }}
                              >
                                <i className="fa fa-trash"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                )}
              </>
            </div>
            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Category <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix={"custom_select_css"}
                  name="CategoryID"
                  value={generalTabData.CategoryOption}
                  options={categoryDrpList}
                  onChange={(event) =>
                    currentUser.UserTypeID !== 8 &&
                    handleDrpChange(event, "CategoryID")
                  }
                  styles={
                    submitted && generalTabData.CategoryID === 0 ? styles : ""
                  }
                  isDisabled={currentUser.UserTypeID === 8} // 🔒 disable if user=8
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Sub Category <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix={"custom_select_css"}
                  name="SubCategoryID"
                  value={generalTabData.SubCategoryOption}
                  options={subCategoryList}
                  onChange={(event) =>
                    currentUser.UserTypeID !== 8 &&
                    handleDrpChange(event, "SubCategoryID")
                  }
                  styles={
                    submitted &&
                    (generalTabData.SubCategoryID === 0 ||
                      !generalTabData.SubCategoryID)
                      ? styles
                      : ""
                  }
                  isDisabled={currentUser.UserTypeID === 8}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Product Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="ProductName"
                  className={`form-control ${
                    submitted && !generalTabData.ProductName && "border-danger"
                  }`}
                  maxLength={50}
                  value={generalTabData.ProductName}
                  placeholder="Product Name"
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  onBlur={(e) => currentUser.UserTypeID !== 8 && handleBlur(e)}
                  readOnly={currentUser.UserTypeID === 8} // 🔒 readonly
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Description <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="Description"
                  value={generalTabData.Description}
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  onBlur={(e) => currentUser.UserTypeID !== 8 && handleBlur(e)}
                  placeholder="Description"
                  maxLength={100}
                  className={`form-control ${
                    submitted && !generalTabData.Description && "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">Short Desc</label>
                <input
                  type="text"
                  name="ShortDescription"
                  maxLength={100}
                  value={generalTabData.ShortDescription}
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  onBlur={(e) => currentUser.UserTypeID !== 8 && handleBlur(e)}
                  placeholder="Short Desc"
                  className="form-control"
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Package Type <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix={"custom_select_css"}
                  name="PackageType"
                  value={generalTabData.PackageTypeOption}
                  options={packageTypeDrpList}
                  onChange={(event) =>
                    currentUser.UserTypeID !== 8 &&
                    handleDrpChange(event, "PackageTypeID")
                  }
                  styles={
                    submitted && generalTabData.PackageTypeID === 0
                      ? styles
                      : ""
                  }
                  isDisabled={currentUser.UserTypeID === 8}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Packages Per Box <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={generalTabData.PerBoxCount}
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  name="PerBoxCount"
                  placeholder="Packages Per Box"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.PerBoxCount ||
                      generalTabData.PerBoxCount <= 0) &&
                    "border-danger"
                  }`}
                  maxLength={10}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Cost <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  onBlur={(e) => currentUser.UserTypeID !== 8 && handleBlur(e)}
                  value={generalTabData.Cost}
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  name="Cost"
                  placeholder="Cost"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.Cost || generalTabData.Cost === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Warranty Period (Months){" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="WarrantyPeriod"
                  maxLength={4}
                  value={generalTabData.WarrantyPeriod}
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  placeholder="Warranty Period"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.WarrantyPeriod ||
                      generalTabData.WarrantyPeriod === 0) &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">Minimum Quantity</label>
                <input
                  type="text"
                  name="MinimumQuantity"
                  maxLength={4}
                  value={generalTabData.MinimumQuantity}
                  onChange={(e) =>
                    currentUser.UserTypeID !== 8 && handleChange(e)
                  }
                  placeholder="Minimum Quantity"
                  className="form-control"
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
            </div>

            <h4 className="text-primary">Price Details</h4>
            {/* <div className="row">

                            <div className="form-group mb-3 col-md-12   ">
                                <label className="text-black font-w500 me-3"> Disable Discount  </label>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={generalTabData.IsDisableDiscount}
                                    onChange={handleCheck}
                                    id="myCheck"
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="customCheckBox1"
                                >
                                    Ignore Price By User Role Discount
                                </label>

                            </div>

                        </div> */}
            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  Regular Price <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="RegularPrice"
                  value={generalTabData.RegularPrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="Regular Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.RegularPrice ||
                      generalTabData.RegularPrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  {" "}
                  G-PARTNER Price <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="GPARTNERPrice"
                  value={generalTabData.GPARTNERPrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="G-PARTNER Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.GPARTNERPrice ||
                      generalTabData.GPARTNERPrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  G-Star Price <span className="text-danger">*</span>{" "}
                </label>
                <input
                  type="text"
                  name="GStarPrice"
                  value={generalTabData.GStarPrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="G-Star Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.GStarPrice ||
                      generalTabData.GStarPrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  PFA Price <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="PFAPrice"
                  value={generalTabData.PFAPrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="PFA Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.PFAPrice ||
                      generalTabData.PFAPrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  {" "}
                  G-Vendor Price <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="GVendorPrice"
                  value={generalTabData.GVendorPrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="G-Vendor Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.GVendorPrice ||
                      generalTabData.GVendorPrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  {" "}
                  G-Associate Price <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="GAssociatePrice"
                  value={generalTabData.GAssociatePrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="G-Associate Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.GAssociatePrice ||
                      generalTabData.GAssociatePrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group mb-3 col-md-4">
                <label className="text-black font-w500">
                  {" "}
                  PartyDeco Price <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="PartyDecoPrice"
                  value={generalTabData.PartyDecoPrice}
                  onBlur={(e) => handleBlur(e)}
                  onChange={(e) => handleChange(e)}
                  placeholder="PartyDeco Price ($)"
                  className={`form-control ${
                    submitted &&
                    (!generalTabData.PartyDecoPrice ||
                      generalTabData.PartyDecoPrice === "$0.00") &&
                    "border-danger"
                  }`}
                  readOnly={currentUser.UserTypeID === 8}
                  style={{
                    backgroundColor:
                      currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                    cursor:
                      currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                  }}
                />
              </div>
            </div>
            <div className="form-group mb-12 col-md-12">
              <label className="text-black font-w500">
                Upload/Add Image (jpeg/jpg/png)
              </label>
              <div className="row">
                {generalTabData &&
                generalTabData.TypeProductImageModelList &&
                generalTabData.TypeProductImageModelList.filter(
                  (d) => d.IsDeleted === false
                ).length > 0 ? (
                  <>
                    {generalTabData &&
                      generalTabData.TypeProductImageModelList.filter(
                        (d) => d.IsDeleted === false
                      ).map((inputField, index) => (
                        <Fragment key={`${inputField}~${index}`}>
                          <div className="col-md-2 mb-2">
                            <div className="card">
                              <div className="card-header">
                                <span className="float-right">
                                  {" "}
                                  <i
                                    onClick={() =>
                                      handleClickPAImageClose(
                                        index,
                                        inputField.ProductImageID
                                      )
                                    }
                                    className="fas fa-times productImageClose"
                                  ></i>
                                </span>
                              </div>
                              <img
                                style={{
                                  height: "170px",
                                  backgroundColor:
                                    currentUser.UserTypeID === 8
                                      ? "#f5f5f5"
                                      : "white",
                                  cursor:
                                    currentUser.UserTypeID === 8
                                      ? "not-allowed"
                                      : "text",
                                }}
                                className="card-img-bottom img-fluid "
                                src={
                                  inputField.ProductImageID > 0
                                    ? REACT_APP_API_IMAGE_URL +
                                      inputField.ImageFilePath
                                    : inputField.ImageFilePath
                                }
                                readOnly={currentUser.UserTypeID === 8}
                                alt="Card_image_cap"
                              />
                            </div>
                          </div>
                        </Fragment>
                      ))}
                  </>
                ) : (
                  <div className="col-md-2 mb-2">
                    <div className="card">
                      <img
                        style={{ height: "170px" }}
                        className="card-img-bottom img-fluid "
                        src={emptyImage}
                        alt="Card_image_cap"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-group mb-3">
                <div className="text-black font-w500">
                  <input
                    type="file"
                    ref={inputRef}
                    accept="image/png, image/jpg, image/jpeg"
                    onChange={(event) => UploadImages(event)}
                    className={`custom-file-input form-control`}
                  />
                </div>
                <span className="input-group-text">Upload</span>
              </div>
            </div>
          </div>

          <h4 className="text-primary">Shipping:</h4>
          <div className="row position-relative display-block">
            <div className="form-group mb-3 col-md-4">
              <label className="text-black font-w500">
                {" "}
                Weight (lbs) <span className="text-danger">*</span>{" "}
              </label>
              <input
                type="text"
                name="Weight"
                value={generalTabData.Weight}
                onChange={(e) => handleChange(e)}
                placeholder="Weight (lbs)"
                className={`form-control ${
                  submitted &&
                  (!generalTabData.Weight ||
                    !parseFloat(
                      generalTabData.Weight && generalTabData.Weight
                    ) > 0) &&
                  "border-danger"
                }`}
                readOnly={currentUser.UserTypeID === 8}
                style={{
                  backgroundColor:
                    currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                  cursor: currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                }}
              />
            </div>
            <div className="form-group mb-3 col-md-4">
              <div>
                <label className="text-black font-w500">
                  {" "}
                  Dimensions (inch) <span className="text-danger">*</span>
                </label>
                <div className="row">
                  <div className="form-group">
                    <input
                      type="text"
                      name="DimensionLength"
                      value={generalTabData.DimensionLength}
                      onChange={(e) => handleChange(e)}
                      placeholder="Length"
                      className={`form-control ${
                        submitted &&
                        (!generalTabData.DimensionLength ||
                          !parseFloat(
                            generalTabData.DimensionLength &&
                              generalTabData.DimensionLength
                          ) > 0) &&
                        "border-danger"
                      }`}
                      readOnly={currentUser.UserTypeID === 8}
                      style={{
                        backgroundColor:
                          currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                        cursor:
                          currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-group mb-3 col-md-4">
              <div>
                <label className="text-black font-w500">
                  {" "}
                  Dimensions (inch) <span className="text-danger">*</span>
                </label>
                <div className="row">
                  <div className="form-group">
                    <input
                      type="text"
                      name="DimensionWidth"
                      value={generalTabData.DimensionWidth}
                      onChange={(e) => handleChange(e)}
                      placeholder="Width"
                      className={`form-control ${
                        submitted &&
                        (!generalTabData.DimensionWidth ||
                          !parseFloat(
                            generalTabData.DimensionWidth &&
                              generalTabData.DimensionWidth
                          ) > 0) &&
                        "border-danger"
                      }`}
                      readOnly={currentUser.UserTypeID === 8}
                      style={{
                        backgroundColor:
                          currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                        cursor:
                          currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group mb-3 col-md-4">
              <div>
                <label className="text-black font-w500">
                  {" "}
                  Dimensions (inch) <span className="text-danger">*</span>
                </label>
                <div className="row">
                  <div className="form-group">
                    <input
                      type="text"
                      name="DimensionHeight"
                      value={generalTabData.DimensionHeight}
                      onChange={(e) => handleChange(e)}
                      placeholder="Height"
                      className={`form-control ${
                        submitted &&
                        (!generalTabData.DimensionHeight ||
                          !parseFloat(
                            generalTabData.DimensionHeight &&
                              generalTabData.DimensionHeight
                          ) > 0) &&
                        "border-danger"
                      }`}
                      readOnly={currentUser.UserTypeID === 8}
                      style={{
                        backgroundColor:
                          currentUser.UserTypeID === 8 ? "#f5f5f5" : "white",
                        cursor:
                          currentUser.UserTypeID === 8 ? "not-allowed" : "text",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group mb-3 col-md-4">
              <label className="text-black font-w500">
                Shipping Methods <span className="text-danger">*</span>
              </label>
              <Select
                classNamePrefix={"custom_select_css"}
                name="ShippingMethodID"
                value={generalTabData.ShippingMethodOption}
                options={shippingMethodDrpList}
                onChange={(event) => handleDrpChange(event, "ShippingMethodID")}
                isDisabled={currentUser.UserTypeID === 8} // ✅ disable for usertype 8
                styles={
                  submitted && generalTabData.ShippingMethodID === 0
                    ? styles
                    : ""
                }
              />
              <span className="validation-text"></span>
            </div>
          </div>

          <button
            className="btn btn-primary mb-250"
            style={{ float: "right" }}
            onClick={tabChange}
            type="submit"
          >
            Next
          </button>
        </div>
      </div>

      {loader}
    </>
  );
};
export default React.memo(GeneralTab);
